// SourcesLine — "Drew on N sources · 1 case study · 1 book excerpt" line
// directly under the reply. Clickable to toggle the ConsultedPanel.
//
// Behavior per the designer Q1-Q2 response:
// - Always show the type breakdown, even for N=1 ("Drew on 1 source · 1 case study").
// - When sources.length === 0: a specific "No knowledge-base sources for
//   this reply" line (absence is information).
// - Click/space/enter toggles the panel open/closed.

import type { CoachCitation } from '@gc-coach/shared';
import { useI18n } from '../i18n/index.js';
import { SourceTypeBadge, type SourceType } from './SourceTypeBadge.js';
import styles from './Coach.module.css';

export interface SourcesLineProps {
  citations: readonly CoachCitation[];
  panelOpen: boolean;
  onToggle: () => void;
}

export function SourcesLine({ citations, panelOpen, onToggle }: SourcesLineProps) {
  const { t, tt } = useI18n();

  if (citations.length === 0) {
    return <div className={styles.srcNone}>{t('sources.none')}</div>;
  }

  const n = citations.length;
  const label =
    n === 1 ? t('sources.lineOne') : tt('sources.line', { n: String(n) });

  // Type counts for the dot-separated badges after the main label.
  const typeCounts = new Map<SourceType, number>();
  for (const c of citations) {
    if (c.type) typeCounts.set(c.type, (typeCounts.get(c.type) ?? 0) + 1);
  }

  return (
    <button
      type="button"
      className={styles.srcLine}
      onClick={onToggle}
      aria-expanded={panelOpen}
    >
      <span className={styles.srcLineLabel}>{label}</span>
      {[...typeCounts.entries()].map(([type, count]) => (
        <SourceTypeBadge
          key={type}
          type={type}
          label={`${count} ${t(`sources.type.${type}`)}`}
        />
      ))}
      <span className={styles.srcLineCaret}>
        {panelOpen ? t('sources.hide') : t('sources.show')}
      </span>
    </button>
  );
}
